// src/pages/Legal.js
// 法務3ページ（特商法・利用規約・プラポリ）の共通レンダラー。
// 本文は lib/legalContent.js。ここは描画だけ。
import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../lib/ThemeContext';
import { IconWarning } from '../lib/icons';
import { LEGAL_DOCS, LEGAL_LINKS } from '../lib/legalContent';

export default function Legal({ doc = 'terms' }) {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const data = LEGAL_DOCS[doc];

  // ページ切り替え時に先頭へ
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [doc]);

  if (!data) return null;

  const s = {
    wrap: { maxWidth: 760, margin: '0 auto' },
    card: {
      background: theme.bgCard,
      border: `1px solid ${theme.border}`,
      borderRadius: 14,
      padding: '28px 26px',
      boxShadow: theme.shadow,
    },
    h1: {
      fontSize: 22,
      fontWeight: 800,
      color: theme.textPrimary,
      letterSpacing: '-0.01em',
      margin: 0,
    },
    updated: { fontSize: 12, color: theme.textWeak, marginTop: 6 },
    intro: {
      fontSize: 13,
      color: theme.textMuted,
      lineHeight: 1.9,
      marginTop: 20,
      paddingTop: 20,
      borderTop: `1px solid ${theme.border}`,
    },
    h2: {
      fontSize: 15,
      fontWeight: 700,
      color: theme.textPrimary,
      marginTop: 32,
      marginBottom: 12,
      paddingBottom: 8,
      borderBottom: `1px solid ${theme.border}`,
    },
    p: { fontSize: 13, color: theme.textSecondary, lineHeight: 1.95, margin: '0 0 12px' },
    li: { fontSize: 13, color: theme.textSecondary, lineHeight: 1.9, marginBottom: 6 },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      fontSize: 12.5,
      margin: '0 0 14px',
    },
    th: {
      textAlign: 'left',
      padding: '9px 12px',
      background: theme.bgInput,
      border: `1px solid ${theme.border}`,
      color: theme.textWeak,
      fontWeight: 700,
      fontSize: 11.5,
      whiteSpace: 'nowrap',
    },
    td: {
      padding: '9px 12px',
      border: `1px solid ${theme.border}`,
      color: theme.textSecondary,
      lineHeight: 1.7,
      verticalAlign: 'top',
      whiteSpace: 'pre-line',
    },
    note: {
      display: 'flex',
      gap: 9,
      alignItems: 'flex-start',
      background: theme.bgInput,
      border: `1px solid ${theme.border}`,
      borderRadius: 10,
      padding: '12px 14px',
      margin: '0 0 14px',
      fontSize: 12.5,
      color: theme.textMuted,
      lineHeight: 1.8,
    },
    footer: {
      marginTop: 36,
      paddingTop: 20,
      borderTop: `1px solid ${theme.border}`,
      display: 'flex',
      gap: 16,
      flexWrap: 'wrap',
    },
    link: {
      background: 'none',
      border: 'none',
      padding: 0,
      fontSize: 12.5,
      color: theme.accent,
      cursor: 'pointer',
      textDecoration: 'underline',
      textUnderlineOffset: 3,
    },
    back: {
      background: 'none',
      border: 'none',
      padding: 0,
      fontSize: 13,
      color: theme.textWeak,
      cursor: 'pointer',
      marginBottom: 16,
    },
  };

  const renderBlock = (b, i) => {
    if (typeof b === 'string') {
      return <p key={i} style={s.p}>{b}</p>;
    }
    if (b.ul) {
      return (
        <ul key={i} style={{ margin: '0 0 14px', paddingLeft: 20 }}>
          {b.ul.map((t, j) => <li key={j} style={s.li}>{t}</li>)}
        </ul>
      );
    }
    if (b.ol) {
      return (
        <ol key={i} style={{ margin: '0 0 14px', paddingLeft: 20 }}>
          {b.ol.map((t, j) => <li key={j} style={s.li}>{t}</li>)}
        </ol>
      );
    }
    if (b.table) {
      return (
        <div key={i} style={{ overflowX: 'auto' }}>
          <table style={s.table}>
            <thead>
              <tr>{b.table.head.map((h, j) => <th key={j} style={s.th}>{h}</th>)}</tr>
            </thead>
            <tbody>
              {b.table.rows.map((row, j) => (
                <tr key={j}>{row.map((c, k) => <td key={k} style={s.td}>{c}</td>)}</tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }
    if (b.note) {
      return (
        <div key={i} style={s.note}>
          <span style={{ flexShrink: 0, lineHeight: 0, marginTop: 2 }}>
            <IconWarning size={15} color={theme.textWeak} />
          </span>
          <span>{b.note}</span>
        </div>
      );
    }
    return null;
  };

  return (
    <div style={s.wrap}>
      <button onClick={() => navigate(-1)} style={s.back}>‹ 戻る</button>

      <div style={s.card}>
        <h1 style={s.h1}>{data.title}</h1>
        <div style={s.updated}>最終更新日：{data.updated}</div>

        {data.intro && <div style={s.intro}>{data.intro}</div>}

        {data.sections.map((sec, i) => (
          <section key={i}>
            {sec.h && <h2 style={s.h2}>{sec.h}</h2>}
            <div style={{ marginTop: sec.h ? 0 : 20 }}>
              {sec.body.map(renderBlock)}
            </div>
          </section>
        ))}

        <div style={s.footer}>
          {LEGAL_LINKS.filter(l => l.key !== doc).map(l => (
            <button key={l.key} onClick={() => navigate(l.path)} style={s.link}>
              {l.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
